import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Send, Cpu, Bot } from "lucide-react";
import { toast } from "sonner";

export default function SpecialistConsole() {
  const { specId } = useParams();
  const [spec, setSpec] = useState(null);
  const [master, setMaster] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/specialists").then((r) => {
      const s = r.data.specialists.find((x) => x.id === specId);
      setSpec(s);
      if (s) api.get(`/agents/${s.master_id}`).then((r2) => setMaster(r2.data));
    });
  }, [specId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, timestamp: new Date().toISOString() }]);
    setSending(true);
    try {
      const { data } = await api.post(`/specialists/${specId}/chat`, { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "agent", content: data.reply, timestamp: data.timestamp }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo al enviar");
    } finally {
      setSending(false);
    }
  };

  if (!spec) return <div className="p-8 text-[var(--text-dim)]" data-testid="spec-loading">Cargando…</div>;

  return (
    <div className="min-h-screen flex flex-col" data-testid="specialist-console">
      <div className="border-b border-[var(--border)] bg-[var(--surface)] px-6 py-4 flex items-center gap-4">
        <Link to="/departments" className="text-[var(--text-dim)] hover:text-white" data-testid="spec-back">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div
          className="w-10 h-10 border flex items-center justify-center"
          style={{ borderColor: master?.color || "#00E5FF", color: master?.color || "#00E5FF" }}
        >
          <Bot className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="label-mono">
            {master?.department || "—"} · SPEC
          </div>
          <div className="font-display font-bold text-lg tracking-tight">{spec.name}</div>
        </div>
        {master && (
          <div className="text-right">
            <div className="label-mono">Master</div>
            <div className="text-sm text-white">{master.name}</div>
            <div className="font-mono text-[10px] text-[var(--text-dim)] flex items-center justify-end gap-1 mt-1">
              <Cpu className="w-3 h-3" /> {master.provider}
            </div>
          </div>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto p-6 space-y-4" data-testid="spec-messages">
        {messages.length === 0 && (
          <div className="max-w-xl mx-auto text-center py-12">
            <div className="w-14 h-14 border border-[var(--accent)] mx-auto mb-4 flex items-center justify-center">
              <Bot className="w-6 h-6 text-[var(--accent)]" />
            </div>
            <div className="font-display font-bold text-2xl mb-2">{spec.name}</div>
            <p className="text-sm text-[var(--text-dim)]">{spec.mission}</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-2xl border px-4 py-3 whitespace-pre-wrap ${
                m.role === "user" ? "border-[var(--accent)]/40 bg-[var(--accent)]/5" : "border-[var(--border)] bg-[var(--surface)]"
              }`}
            >
              <div className="label-mono mb-2">{m.role === "user" ? "TÚ" : spec.id.toUpperCase()}</div>
              <div className="text-sm leading-relaxed">{m.content}</div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="border border-[var(--border)] bg-[var(--surface)] px-4 py-3 max-w-md">
            <div className="label-mono mb-1">{spec.id.toUpperCase()} · procesando</div>
            <div className="font-mono text-sm text-[var(--accent)] cursor-blink">consultando</div>
          </div>
        )}
      </div>

      <div className="border-t border-[var(--border)] bg-[var(--surface)] p-4">
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder={`Ordena a ${spec.name}…`}
            className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-sm min-h-[70px] resize-none"
            data-testid="spec-input"
          />
          <Button
            onClick={send}
            disabled={sending || !input.trim()}
            className="rounded-none h-[70px] bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 font-semibold px-6"
            data-testid="spec-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
